import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { usePermissions } from '../hooks/usePermissions';
import { COMPONENT_REGISTRY } from '../config/componentRegistry';
import { useHelp } from './HelpProvider';
import { validateConfig } from './helpCore';
import Markdown from './Markdown';
import index from './fieldIndex.generated.json';
import './help.css';

const roleNames = [...new Set(Object.values(COMPONENT_REGISTRY).flatMap((c) =>
  [...(c.viewRoles || []), ...(c.editRoles || []), ...(c.defaultRoles || [])]))].sort();
function Roles({ value, onChange, label }) {
  return <Form.Group className="mb-3"><Form.Label>{label}</Form.Label>
    <Form.Select multiple aria-label={label} value={value}
      onChange={(e) => onChange(Array.from(e.target.selectedOptions).map((o) => o.value))}>
      {[...new Set([...roleNames, ...value])].sort().map((r) => <option key={r}>{r}</option>)}
    </Form.Select><Form.Text>No selected roles means all authenticated roles. Use Ctrl/Cmd to select multiple.</Form.Text>
    <Button size="sm" variant="link" onClick={() => onChange([])}>Allow all roles</Button>
  </Form.Group>;
}
function download(name, content, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement('a');
  link.href = url; link.download = name; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export default function AdminHelpManager() {
  const { config, save, reset, warning } = useHelp();
  const { canEdit } = usePermissions();
  const isAdmin = useSelector((s) => s.user.status === 'succeeded' && s.user.rolesMapped?.Admin === true);
  const [draft, setDraft] = useState(config);
  const [selected, setSelected] = useState(config.articles[0]?.id || '');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { setDraft(config); }, [config]);
  if (!isAdmin || !canEdit('admin.help-manager')) return <Alert variant="warning">Help management requires administrator edit access.</Alert>;
  const article = draft.articles.find((a) => a.id === selected);
  const updateArticle = (patch) => setDraft((d) => ({ ...d, articles: d.articles.map((a) => a.id === selected ? { ...a, ...patch } : a) }));
  const run = (action) => {
    setError(''); setMessage('');
    try { action(); } catch (e) { setError(e.message); }
  };
  const importFile = async (event, markdown) => {
    const file = event.target.files[0]; event.target.value = '';
    if (!file) return;
    setError(''); setMessage('');
    try {
      if (file.size > (markdown ? 100000 : 2000000)) throw new Error('File is too large.');
      const text = await file.text();
      if (markdown) updateArticle({ markdown: text });
      else {
        const imported = validateConfig(JSON.parse(text));
        setDraft(imported); setSelected(imported.articles[0]?.id || '');
      }
      setMessage('Imported into the editor. Save the browser draft to apply.');
    } catch (e) { setError(e.message); }
  };
  return <div className="portal-help-admin" data-portal-help>
    <h3>Help manager</h3>
    <p>Control the help drawer and maintain searchable instructions.</p>
    <Alert variant="info"><strong>Browser-local draft.</strong> Saves apply only to this account, environment, and browser.
      Export a package for review or deployment. Publishing changes to everyone requires backend storage.
      Visibility settings control this help UI; they do not grant API access.</Alert>
    {warning && <Alert variant="warning">{warning}</Alert>}
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {message && <Alert variant="success" role="status">{message}</Alert>}
    <div className="d-flex flex-wrap gap-2 mb-3">
      <Button onClick={() => run(() => { save(draft); setMessage('Browser draft saved. Other users are unchanged.'); })}>Save browser draft</Button>
      <Button variant="outline-primary" onClick={() => run(() => {
        validateConfig(draft); download('portal-help-package.json', JSON.stringify(draft, null, 2), 'application/json');
      })}>Export package</Button>
      <Button variant="outline-secondary" onClick={() => run(() => { reset(); setMessage('Bundled defaults restored.'); })}>Restore bundled defaults</Button>
    </div>
    <Form.Group controlId="help-import-package" className="mb-4">
      <Form.Label>Import help package (replaces editor contents)</Form.Label>
      <Form.Control type="file" accept=".json,application/json" onChange={(e) => importFile(e, false)} />
    </Form.Group>
    <Card className="mb-3"><Card.Body>
      <h4>Visibility</h4>
      <Alert variant="secondary">Help is restricted to Admin users inside the admin container. These settings can further restrict visibility, but cannot enable it on other pages or for non-admin users.</Alert>
      <Form.Check id="help-enabled" type="switch" label="Enable help drawer" checked={draft.enabled}
        onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} className="mb-3" />
      <Roles label="Global allowed roles" value={draft.roles} onChange={(roles) => setDraft({ ...draft, roles })} />
      <p>Page patterns: <code>/:jobId/elecInfo</code>, <code>/production/shipping/*</code>, or <code>*</code>.
        Unlisted pages follow the global setting. Every matching rule must allow access.</p>
      {draft.pageRules.map((r, i) => <Card key={i} className="mb-2"><Card.Body>
        <Form.Control aria-label={`Page pattern ${i + 1}`} value={r.page} onChange={(e) =>
          setDraft({ ...draft, pageRules: draft.pageRules.map((p, j) => j === i ? { ...p, page: e.target.value } : p) })} />
        <Form.Check id={`help-rule-${i}`} label="Show help on matching pages" checked={r.enabled} onChange={(e) =>
          setDraft({ ...draft, pageRules: draft.pageRules.map((p, j) => j === i ? { ...p, enabled: e.target.checked } : p) })} />
        <Roles label={`Allowed roles for rule ${i + 1}`} value={r.roles} onChange={(roles) =>
          setDraft({ ...draft, pageRules: draft.pageRules.map((p, j) => j === i ? { ...p, roles } : p) })} />
        <Button variant="outline-danger" size="sm" onClick={() => setDraft({ ...draft, pageRules: draft.pageRules.filter((_, j) => j !== i) })}>Remove rule</Button>
      </Card.Body></Card>)}
      <Button variant="outline-primary" onClick={() => setDraft({ ...draft, pageRules: [...draft.pageRules, { page: '/', enabled: true, roles: [] }] })}>Add page rule</Button>
    </Card.Body></Card>
    <Card className="mb-3"><Card.Body>
      <h4>Markdown articles</h4>
      <div className="d-flex gap-2 mb-3">
        <Form.Select aria-label="Choose help article" value={article?.id || ''} onChange={(e) => setSelected(e.target.value)}>
          <option value="">Choose an article</option>
          {draft.articles.map((a) => <option value={a.id} key={a.id}>{a.title}</option>)}
        </Form.Select>
        <Button onClick={() => {
          const id = `article-${Date.now()}`;
          setDraft({ ...draft, articles: [...draft.articles, { id, title: 'New article', page: '*', roles: [], markdown: '# New instructions\n' }] });
          setSelected(id);
        }}>New</Button>
      </div>
      {article && <>
        <Form.Group controlId="help-article-title" className="mb-2"><Form.Label>Title</Form.Label>
          <Form.Control value={article.title} maxLength={199} onChange={(e) => updateArticle({ title: e.target.value })} /></Form.Group>
        <Form.Group controlId="help-article-page" className="mb-2"><Form.Label>Show article on pages matching</Form.Label>
          <Form.Control value={article.page} onChange={(e) => updateArticle({ page: e.target.value })} /></Form.Group>
        <Roles label="Article audience" value={article.roles} onChange={(roles) => updateArticle({ roles })} />
        <Row><Col md={6}><Form.Group controlId="help-markdown"><Form.Label>Markdown source</Form.Label>
          <Form.Control as="textarea" rows={16} maxLength={100000} value={article.markdown} onChange={(e) => updateArticle({ markdown: e.target.value })} />
          <Form.Text>Preview supports headings, paragraphs, bullets, and bold. HTML is displayed as text.</Form.Text>
        </Form.Group></Col><Col md={6}><h5>Preview</h5><Markdown text={article.markdown} /></Col></Row>
        <Form.Group controlId="help-import-md" className="my-3"><Form.Label>Import Markdown into this article</Form.Label>
          <Form.Control type="file" accept=".md,text/markdown,text/plain" onChange={(e) => importFile(e, true)} /></Form.Group>
        <Button variant="outline-primary" onClick={() => download('help-article.md', article.markdown, 'text/markdown')}>Export Markdown</Button>{' '}
        <Button variant="outline-danger" onClick={() => { setDraft({ ...draft, articles: draft.articles.filter((a) => a.id !== selected) }); setSelected(''); }}>Delete article from draft</Button>
      </>}
    </Card.Body></Card>
    <Card><Card.Body><h4>Generated field directory</h4>
      <p>{index.fields.length} static field occurrences from {index.scanned} component files.
        Regenerate with <code>npm run generate:help-index</code>. The reviewable Markdown index is in <code>docs/help/search-index.md</code>.</p>
      <p className="mb-0">The generated directory records source locations, dropdown types, and component API references.
        Dynamic fields and exact write endpoints still require review. Add articles above to fill documentation gaps.</p>
    </Card.Body></Card>
  </div>;
}
